import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, ShoppingBag, Eye } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { useCart } from '../../context/CartContext';
import { formatCurrency } from '../../lib/currency';
import { cn } from '../../lib/utils';

const IMAGE_INTERVAL_MS = 2500;
const SWIPE_THRESHOLD = 40;
const GESTURE_LOCK_PX = 6;

const badgeClass =
  'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-normal text-white backdrop-blur-sm sm:px-2 sm:py-1 sm:text-xs';

const gridPriceFormat = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

const ProductCard = ({
  product,
  showQuickAdd = true,
  showQuickView = false,
  variant = 'carousel',
}) => {
  const { t } = useTranslation();
  const { toggleWishlist, isInWishlist, addToCart } = useCart();
  const images = useMemo(() => {
    const raw = Array.isArray(product?.images)
      ? product.images
      : product?.images
        ? [product.images]
        : [];
    return raw.filter(Boolean);
  }, [product?.images]);
  const sizes = product?.sizes || [];
  const colorNames = product?.colorNames || [];
  const inWishlist = isInWishlist(product?.id);
  const discountAmount =
    product?.originalPrice && product?.price && product.originalPrice > product.price
      ? product.originalPrice - product.price
      : 0;
  const [activeImage, setActiveImage] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const hoverIntervalRef = useRef(null);
  const swipeRef = useRef(null);
  const swipedRef = useRef(false);
  const gestureRef = useRef({
    startX: 0,
    startY: 0,
    axis: null,
    active: false,
  });
  const activeImageRef = useRef(0);

  const isGrid = variant === 'grid';
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    activeImageRef.current = activeImage;
  }, [activeImage]);

  useEffect(() => {
    setActiveImage(0);
    setDragOffset(0);
  }, [product?.id]);

  const stepImage = useCallback(
    (delta) => {
      setActiveImage((prev) => {
        const next = prev + delta;
        if (next < 0 || next >= images.length) return prev;
        return next;
      });
    },
    [images.length]
  );

  const startHoverCycle = useCallback(() => {
    setIsHovered(true);
    if (isGrid || !hasMultipleImages) return;
    if (hoverIntervalRef.current) clearInterval(hoverIntervalRef.current);
    hoverIntervalRef.current = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % images.length);
    }, IMAGE_INTERVAL_MS);
  }, [isGrid, hasMultipleImages, images.length]);

  const stopHoverCycle = useCallback(() => {
    setIsHovered(false);
    if (hoverIntervalRef.current) {
      clearInterval(hoverIntervalRef.current);
      hoverIntervalRef.current = null;
    }
    if (!isGrid) {
      setActiveImage(0);
    }
  }, [isGrid]);

  useEffect(() => {
    return () => {
      if (hoverIntervalRef.current) clearInterval(hoverIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    const el = swipeRef.current;
    if (!el || !hasMultipleImages) return;

    const applyDragOffset = (dx) => {
      const current = activeImageRef.current;
      let offset = dx;
      if ((current === 0 && offset > 0) || (current === images.length - 1 && offset < 0)) {
        offset *= 0.35;
      }
      setDragOffset(offset);
    };

    const finishSwipe = (dx) => {
      if (gestureRef.current.axis !== 'x' || Math.abs(dx) < SWIPE_THRESHOLD) return;

      if (dx < 0 && activeImageRef.current < images.length - 1) {
        stepImage(1);
        swipedRef.current = true;
      } else if (dx > 0 && activeImageRef.current > 0) {
        stepImage(-1);
        swipedRef.current = true;
      }
    };

    const resetGesture = () => {
      gestureRef.current = {
        startX: 0,
        startY: 0,
        axis: null,
        active: false,
      };
      el.classList.remove('is-swiping-x');
      setIsDragging(false);
      setDragOffset(0);
    };

    const resolveAxis = (dx, dy) => {
      const gesture = gestureRef.current;
      if (gesture.axis) return gesture.axis;
      if (Math.abs(dx) < GESTURE_LOCK_PX && Math.abs(dy) < GESTURE_LOCK_PX) return null;

      // Hafif çapraz hareketlerde yatay kaydırmayı tercih et
      gesture.axis = Math.abs(dx) >= Math.abs(dy) * 0.85 ? 'x' : 'y';
      if (gesture.axis === 'x') {
        el.classList.add('is-swiping-x');
        setIsDragging(true);
      }
      return gesture.axis;
    };

    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      gestureRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        axis: null,
        active: true,
      };
      swipedRef.current = false;
    };

    const onTouchMove = (e) => {
      const gesture = gestureRef.current;
      if (!gesture.active || e.touches.length !== 1) return;

      const dx = e.touches[0].clientX - gesture.startX;
      const dy = e.touches[0].clientY - gesture.startY;
      const axis = resolveAxis(dx, dy);

      if (axis === 'x') {
        e.preventDefault();
        e.stopPropagation();
        applyDragOffset(dx);
      }
    };

    const onTouchEnd = (e) => {
      const gesture = gestureRef.current;
      if (!gesture.active) return;

      const endX = e.changedTouches[0]?.clientX ?? gesture.startX;
      finishSwipe(endX - gesture.startX);
      resetGesture();
    };

    const onPointerDown = (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      gestureRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        axis: null,
        active: true,
      };
      swipedRef.current = false;
    };

    const onPointerMove = (e) => {
      const gesture = gestureRef.current;
      if (!gesture.active || e.pointerType !== 'mouse') return;

      const dx = e.clientX - gesture.startX;
      const dy = e.clientY - gesture.startY;
      const axis = resolveAxis(dx, dy);

      if (axis === 'x') {
        e.preventDefault();
        applyDragOffset(dx);
      }
    };

    const onPointerUp = (e) => {
      const gesture = gestureRef.current;
      if (!gesture.active || e.pointerType !== 'mouse') return;

      finishSwipe(e.clientX - gesture.startX);
      resetGesture();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.classList.remove('is-swiping-x');
    };
  }, [hasMultipleImages, images.length, stepImage]);

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, sizes[0] || null, colorNames[0] || null, 1);
  };

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
  };

  const trackTransform = hasMultipleImages
    ? `translateX(calc(-${activeImage * (100 / images.length)}% + ${dragOffset}px))`
    : 'translateX(0)';

  return (
    <div className="group flex h-full w-full min-w-0 flex-col">
      <Card
        className={cn(
          'relative w-full shrink-0 overflow-hidden border-0 bg-gray-100',
          isGrid
            ? 'aspect-[3/5] rounded-none shadow-none'
            : 'aspect-[3/4] rounded-md shadow-sm transition-shadow duration-300 hover:shadow-md focus-within:ring-2 focus-within:ring-gray-900 focus-within:ring-offset-2'
        )}
        onMouseEnter={startHoverCycle}
        onMouseLeave={stopHoverCycle}
      >
        <div
          ref={swipeRef}
          className="product-card-swipe absolute inset-0 h-full w-full select-none"
          role="img"
          aria-label={
            hasMultipleImages
              ? `${product?.name}, görsel ${activeImage + 1} / ${images.length}`
              : product?.name
          }
        >
          <Link
            to={`/product/${product.id}`}
            className="absolute inset-0 z-0 block"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            onClick={(e) => {
              if (swipedRef.current || isDragging) {
                e.preventDefault();
                swipedRef.current = false;
              }
            }}
          >
            <motion.div
              className="absolute inset-0 h-full w-full overflow-hidden"
              animate={{ scale: isHovered && !isGrid ? 1.03 : 1 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <div
                className={cn(
                  'flex h-full',
                  isDragging ? 'transition-none' : 'transition-transform duration-300 ease-out'
                )}
                style={{
                  width: `${Math.max(images.length, 1) * 100}%`,
                  transform: trackTransform,
                }}
              >
                {images.map((img, i) => (
                  <div
                    key={`${product?.id}-${i}-${img}`}
                    className="relative h-full flex-shrink-0 overflow-hidden"
                    style={{ width: `${100 / Math.max(images.length, 1)}%` }}
                  >
                    <img
                      src={img}
                      alt={`${product?.name}${hasMultipleImages ? ` - ${i + 1}/${images.length}` : ''}`}
                      className="absolute inset-0 h-full w-full object-cover object-center pointer-events-none"
                      loading={i === 0 ? 'eager' : 'lazy'}
                      draggable={false}
                    />
                  </div>
                ))}
              </div>
            </motion.div>
          </Link>

          {hasMultipleImages && (
            <div
              className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1"
              aria-hidden="true"
            >
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveImage(i);
                  }}
                  className={`h-1 w-1 rounded-full transition-colors sm:h-1.5 sm:w-1.5 ${
                    i === activeImage ? 'bg-white' : 'bg-white/50'
                  }`}
                  aria-label={`Görsel ${i + 1}`}
                />
              ))}
            </div>
          )}

          <div className="absolute left-2 top-2 z-10 flex flex-col gap-1">
            {product.onSale && (
              <span className={`${badgeClass} bg-red-500/55`}>{t('common.sale')}</span>
            )}
            {product.isNew && (
              <span className={`${badgeClass} bg-blue-600/55`}>{t('product.new')}</span>
            )}
          </div>

          {product.isBestSeller && (
            <span
              className={`absolute bottom-1.5 right-1.5 z-10 sm:bottom-2 sm:right-2 ${badgeClass} bg-[#1a1a2e]/55`}
            >
              {t('home.bestSellers')}
            </span>
          )}

          <button
            type="button"
            onClick={handleWishlistClick}
            className={cn(
              'absolute z-10 flex items-center justify-center rounded-full border border-white/40 bg-white/75 shadow-sm backdrop-blur-sm transition-all hover:bg-white',
              isGrid
                ? 'right-1.5 top-1.5 h-7 w-7'
                : 'right-2 top-2 h-9 w-9 sm:h-10 sm:w-10'
            )}
            aria-label={inWishlist ? t('product.removedFromWishlist') : t('product.addedToWishlist')}
          >
            <Heart
              size={isGrid ? 14 : 18}
              className={inWishlist ? 'fill-red-500 text-red-500' : 'text-gray-600'}
            />
          </button>

          {showQuickAdd && !isGrid && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 z-20 hidden p-3 md:block"
              initial={{ y: '100%', opacity: 0 }}
              animate={{
                y: isHovered ? 0 : '100%',
                opacity: isHovered ? 1 : 0,
              }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <Button
                onClick={handleQuickAdd}
                className="w-full rounded-md border border-white/40 bg-white/25 font-semibold text-white shadow-lg backdrop-blur-md hover:bg-white/40"
              >
                <ShoppingBag size={16} className="mr-2" />
                {t('common.addToCart')}
              </Button>
            </motion.div>
          )}

          {showQuickView && !isGrid && (
            <motion.div
              className="absolute bottom-16 left-0 right-0 z-20 hidden px-3 md:block"
              initial={{ opacity: 0 }}
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.25 }}
            >
              <Link to={`/product/${product.id}`}>
                <Button
                  variant="outline"
                  className="w-full rounded-md border-gray-200 bg-white/95 hover:bg-white"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Eye size={16} className="mr-2" />
                  Hızlı Görüntüle
                </Button>
              </Link>
            </motion.div>
          )}
        </div>
      </Card>

      <div
        className={cn(
          isGrid ? 'px-3 pb-1.5 pt-2' : 'mt-2 flex min-h-[3.5rem] flex-1 flex-col gap-0.5 px-0.5 sm:mt-3'
        )}
      >
        <Link to={`/product/${product.id}`} className="block min-w-0">
          <h3
            className={cn(
              'truncate transition-colors hover:text-black',
              isGrid
                ? 'text-[13px] font-normal leading-snug text-gray-900'
                : 'line-clamp-2 overflow-hidden text-sm font-medium leading-snug text-gray-800 hover:text-[#1a1a2e]'
            )}
          >
            {product?.name}
          </h3>
        </Link>

        {isGrid ? (
          <div className="mt-1 space-y-0.5">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0">
              <span className="text-[15px] font-semibold leading-none text-gray-950">
                {formatCurrency(product?.price, gridPriceFormat)}
              </span>
              {product?.originalPrice && product.originalPrice > product?.price && (
                <span className="text-xs font-normal text-gray-500 line-through">
                  {formatCurrency(product.originalPrice, gridPriceFormat)}
                </span>
              )}
            </div>
            {discountAmount > 0 && (
              <p className="text-xs font-normal text-red-600">
                -{formatCurrency(discountAmount, gridPriceFormat)} indirim
              </p>
            )}
          </div>
        ) : (
          <div className="mt-auto flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(product?.price)}
            </span>
            {product?.originalPrice && product.originalPrice > product?.price && (
              <span className="text-xs font-light text-gray-400 line-through">
                {formatCurrency(product.originalPrice)}
              </span>
            )}
            {discountAmount > 0 && (
              <span className="text-[10px] font-medium text-red-600 sm:text-xs">
                -{formatCurrency(discountAmount)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
